import {faArrowsRotate, faTrash} from '@fortawesome/pro-regular-svg-icons';
import {faEye} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Image, Popover, Typography} from 'antd';
import {useCallback, useState} from 'react';

import {Locale} from '../../../../../../../../provider/locale/localization';
import {CompanyImageFormType} from '../../../../../../../../service/company-v2/types/company-logo-type';
import {classNames} from '../../../../../../../../util/css';

import * as styles from './gallery-image.scss';

type PropsType = {
    inputImage: CompanyImageFormType;
    className?: string;
    errorMessage?: Array<string>;
    modifications?: Array<string>;
    onRemove: () => void;
};

export function GalleryImage(props: PropsType): JSX.Element {
    const {className, inputImage, errorMessage, modifications, onRemove} = props;
    const {objectUrl, url} = inputImage;

    const fullClassName = classNames(styles.input_image_list_item, className);

    const [previewOpen, setPreviewOpen] = useState(false);
    const [popoverOpen, setPopoverOpen] = useState(false);
    const [modificationsPopoverOpen, setModificationsPopoverOpen] = useState(false);

    const handleDelete = useCallback(() => {
        if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
        }

        onRemove();
    }, [objectUrl, onRemove]);

    const image = (
        <>
            <Image
                className={styles.input_image_list_item__image}
                preview={{
                    visible: previewOpen,
                    onVisibleChange: (value) => setPreviewOpen(value),
                }}
                src={(url || objectUrl) ?? undefined} // eslint-disable-line no-undefined
            />

            <div className={styles.input_image_list_item__overlay}>
                <Button
                    className={styles.input_image_list_item__button}
                    icon={<FontAwesomeIcon icon={faEye} />}
                    onClick={() => {
                        setPreviewOpen(true);
                        setPopoverOpen(false);
                    }}
                    type="text"
                />

                <Button
                    className={styles.input_image_list_item__button}
                    icon={<FontAwesomeIcon icon={faTrash} />}
                    onClick={handleDelete}
                    type="text"
                />
            </div>
        </>
    );

    if (errorMessage) {
        return (
            <div className={fullClassName}>
                <Popover
                    content={
                        <>
                            <Typography.Paragraph className={styles.error_text}>
                                <Locale stringKey="COMPANY_FORM__GALLERY__ERROR__DESCRIPTION" />
                            </Typography.Paragraph>

                            {errorMessage.map((message) => (
                                <Typography.Paragraph className={styles.error_text} key={message}>
                                    {message}
                                </Typography.Paragraph>
                            ))}
                        </>
                    }
                    onOpenChange={(value) => setPopoverOpen(value)}
                    open={popoverOpen}
                    title={<Locale stringKey="COMPANY_FORM__GALLERY__ERROR__HEADER" />}
                >
                    {/* https://github.com/ant-design/ant-design/issues/15909#issuecomment-1030778255 */}
                    <div className={styles.input_image_list_item__image}>{image}</div>
                </Popover>
            </div>
        );
    }

    if (modifications && modifications.length > 0) {
        return (
            <div className={fullClassName}>
                <Popover
                    content={
                        <ul className={styles.modifications_list}>
                            {modifications.map((message) => (
                                <li key={message}>
                                    <Typography.Text className={styles.error_text}>{message}</Typography.Text>
                                </li>
                            ))}
                        </ul>
                    }
                    onOpenChange={(value) => setModificationsPopoverOpen(value)}
                    open={modificationsPopoverOpen}
                    title={<Locale stringKey="COMPANY_FORM__GALLERY__MODIFICATIONS__HEADER" />}
                    zIndex={2}
                >
                    {/* https://github.com/ant-design/ant-design/issues/15909#issuecomment-1030778255 */}
                    <div className={styles.input_image_list_item__image}>
                        <FontAwesomeIcon
                            className={styles.input_image_list_item__modifications}
                            color="white"
                            icon={faArrowsRotate}
                        />
                        {image}
                    </div>
                </Popover>
            </div>
        );
    }

    return <div className={fullClassName}>{image}</div>;
}
